import { useCallback, useEffect, type RefObject } from 'react';

export const CANVAS_PREVIEW_REFRESH_EVENT = 'landi-canvas-preview-refresh';

export interface CanvasPreviewRefreshDetail {
  siteId: string;
  reason?: string;
}

/** Dispatch a refresh signal when draft HTML may have changed. */
export function dispatchCanvasPreviewRefresh(detail: CanvasPreviewRefreshDetail): void {
  window.dispatchEvent(
    new CustomEvent<CanvasPreviewRefreshDetail>(CANVAS_PREVIEW_REFRESH_EVENT, { detail }),
  );
}

/**
 * Isolated iframe messaging for Canvas Studio WebPanel.
 * Listens for preview refresh events and forwards postMessage payloads when needed.
 */
export function useIframeMessaging(
  iframeRef: RefObject<HTMLIFrameElement | null>,
  siteId: string | null,
  onRefreshRequest?: () => void,
): { postToIframe: (message: { type: string; data?: unknown }) => void } {
  useEffect(() => {
    const handler = (event: Event) => {
      const detail = (event as CustomEvent<CanvasPreviewRefreshDetail>).detail;
      if (!detail?.siteId || !siteId || detail.siteId !== siteId) {
        return;
      }
      onRefreshRequest?.();
    };
    window.addEventListener(CANVAS_PREVIEW_REFRESH_EVENT, handler);
    return () => window.removeEventListener(CANVAS_PREVIEW_REFRESH_EVENT, handler);
  }, [siteId, onRefreshRequest]);

  const postToIframe = useCallback(
    (message: { type: string; data?: unknown }) => {
      const target = iframeRef.current?.contentWindow;
      if (!target) {
        return;
      }
      try {
        target.postMessage(message, '*');
      } catch (err) {
        console.error('[useIframeMessaging] postMessage failed', err);
      }
    },
    [iframeRef],
  );

  return { postToIframe };
}
